/**
 * Mission-First grouping for the All Teams Dashboard.
 *
 * Missions (item_type === 'rock') are stored as one row per assignee. To show a
 * mission once, we group those rows by `mission_id`. Legacy rows created before
 * mission_id existed fall back to a stable title+quarter key so they still collapse.
 */

const titleKey = (task) =>
    `title:${String(task.title || '')
        .trim()
        .toLowerCase()}|${task.quarter || ''}`;

const missionKey = (task) => task.mission_id || titleKey(task);

/** Treat boolean true and legacy string "true" as captain. */
const isCaptainRow = (task) =>
    task?.is_captain === true || task?.is_captain === 'true';

/** Stable order for default captain = earliest assignee row. */
const sortAssigneesForCaptainDefault = (assignees) =>
    [...assignees].sort((a, b) => {
        const ta = a.created_at || '';
        const tb = b.created_at || '';
        if (ta !== tb) return ta.localeCompare(tb);
        return String(a.assignee_email || '').localeCompare(
            String(b.assignee_email || '')
        );
    });

/**
 * Resolve captain: saved is_captain row, else first assignee (default).
 * @returns {{ row: object|null, isExplicit: boolean }}
 */
export function resolveCaptain(assignees = []) {
    const explicit = assignees.find((a) => isCaptainRow(a));
    if (explicit) {
        return { row: explicit, isExplicit: true };
    }
    const sorted = sortAssigneesForCaptainDefault(assignees);
    if (sorted.length === 0) {
        return { row: null, isExplicit: false };
    }
    return { row: sorted[0], isExplicit: false };
}

const normalizeEmail = (email) =>
    String(email || '')
        .trim()
        .toLowerCase();

/** Whether this assignee row is the mission's displayed captain. */
export function isMissionCaptain(task, captainRow) {
    if (!task || !captainRow) return false;
    if (task.id && captainRow.id && task.id === captainRow.id) return true;
    return (
        normalizeEmail(task.assignee_email) ===
        normalizeEmail(captainRow.assignee_email)
    );
}

const displayName = (task) => {
    const name = `${task.assignee_first_name || ''} ${
        task.assignee_last_name || ''
    }`.trim();
    return name || task.assignee_email || 'Unknown';
};

/**
 * @param {Array} rocks - top-level mission rows (item_type === 'rock')
 * @returns {Array} one entry per mission with aggregated metadata
 */
export function groupMissions(rocks = []) {
    const map = new Map();

    rocks.forEach((task) => {
        const key = missionKey(task);
        let group = map.get(key);
        if (!group) {
            group = {
                key,
                mission_id: task.mission_id || null,
                title: task.title,
                quarter: task.quarter || null,
                due_date: task.due_date?.value || task.due_date || null,
                assignees: []
            };
            map.set(key, group);
        }
        group.assignees.push(task);

        // Keep the earliest non-empty due date for the mission header
        const due = task.due_date?.value || task.due_date || null;
        if (due && (!group.due_date || due < group.due_date)) {
            group.due_date = due;
        }
    });

    return Array.from(map.values()).map((group) => {
        // Total Assigned = distinct assignees on the mission
        const distinctAssignees = new Set(
            group.assignees.map((a) =>
                String(a.assignee_email || '')
                    .trim()
                    .toLowerCase()
            )
        );
        const totalAssigned = distinctAssignees.size || group.assignees.length;

        const completedCount = group.assignees.filter(
            (a) => a.status === 'completed'
        ).length;

        const { row: captainRow, isExplicit: captainIsExplicit } =
            resolveCaptain(group.assignees);

        const hasOverdue = group.assignees.some(
            (a) => a.status === 'needsAction' && a.sla?.is_overdue
        );

        const allCompleted =
            group.assignees.length > 0 &&
            completedCount === group.assignees.length;

        return {
            ...group,
            total_assigned: totalAssigned,
            completed_count: completedCount,
            is_complete: allCompleted,
            is_overdue: hasOverdue,
            captain: captainRow || null,
            captain_name: captainRow ? displayName(captainRow) : null,
            captain_is_explicit: captainIsExplicit
        };
    });
}

/** Mission-level stat counts for the summary cards. */
export function getMissionStats(missionGroups = []) {
    const total = missionGroups.length;
    const completed = missionGroups.filter((m) => m.is_complete).length;
    const overdue = missionGroups.filter((m) => m.is_overdue).length;
    return {
        total,
        completed,
        pending: total - completed,
        overdue
    };
}

export { displayName as missionAssigneeName, isCaptainRow };
